import React, { useState, useRef } from "react";
import { Modal, Button, Form, Table, Spinner, Badge, Card, Row, Col } from "react-bootstrap";
import { FiUpload, FiFile, FiUser, FiCalendar, FiCreditCard, FiCheckCircle } from "react-icons/fi";
import axios from "axios";
import { toast } from "react-toastify";

const UploadModal = ({ show, onHide, onUploadSuccess }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [summary, setSummary] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const fileExtension = selectedFile.name.toLowerCase();
        const isValidExtension = fileExtension.match(/\.(xlsx|xls|csv|txt)$/);

        if (!isValidExtension) {
            toast.error("Please select a valid Excel or text file");
            return;
        }

        setFile(selectedFile);
        setPreviewData([]);
        setHeaders([]);
        setTotalRows(0);
        setSummary(null);
        parseFile(selectedFile);
    };

    const parseFile = async (file) => {
        try {
            await parseWithSheetJS(file);
        } catch (error) {
            toast.error("Error parsing file: " + error.message);
        }
    };

    const parseWithSheetJS = async (file) => {
        try {
            const XLSX = await import('xlsx');

            const reader = new FileReader();
            reader.onload = function (e) {
                try {
                    const fileData = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(fileData, { type: 'array', cellDates: true, raw: true });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    if (!worksheet['!ref']) {
                        toast.error("No data found in the worksheet");
                        return;
                    }

                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        defval: '',
                        blankrows: false
                    });

                    if (jsonData.length === 0) {
                        toast.error("File is empty");
                        return;
                    }

                    const headerRowIndex = 2;

                    if (jsonData.length <= headerRowIndex) {
                        toast.error("No data rows found after headers");
                        return;
                    }

                    const fileHeaders = jsonData[headerRowIndex].map((header, index) => {
                        return String(header || `Column_${index + 1}`).trim();
                    });

                    const parsedData = [];

                    for (let i = 3; i < jsonData.length - 2; i++) {
                        const row = jsonData[i];
                        if (!row || !Array.isArray(row)) continue;

                        const rowData = {};
                        let hasData = false;

                        fileHeaders.forEach((header, colIndex) => {
                            const value = row[colIndex] !== undefined ? String(row[colIndex]).trim() : '';
                            rowData[header] = value;
                            if (value !== '') hasData = true;
                        });

                        if (hasData) {
                            parsedData.push({ ...rowData, _rowNumber: parsedData.length + 1 });
                        }
                    }

                    const summaryData = generateSummary(parsedData, fileHeaders);

                    // Now set all states
                    setHeaders(fileHeaders);
                    setTotalRows(parsedData.length);
                    setPreviewData(parsedData.slice(0, 25));
                    setSummary(summaryData);

                    if (parsedData.length === 0) {
                        toast.warning("Detected headers but no data rows found");
                    } else {
                        toast.success(`Detected ${fileHeaders.length} columns and ${parsedData.length} rows`);
                    }

                } catch (error) {
                    toast.error("Error processing file: " + error.message);
                }
            };

            reader.onerror = function (error) {
                toast.error("Error reading file: " + error.message);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            toast.error("Error loading file parser: " + error.message);
        }
    };

    const generateSummary = (data, fileHeaders) => {
        if (!data.length || !fileHeaders) return null;

        const summaryArray = data.map(row => {
            const payeeName = findColumnValue(row, fileHeaders, ['payee name', 'payee', 'name']);
            const paymentDate = findColumnValue(row, fileHeaders, ['payment date', 'date']);
            const accountNumber = findColumnValue(row, fileHeaders, ['payee account number']);
            const status = findColumnValue(row, fileHeaders, ['status']);

            return {
                payeeName: payeeName || '',
                paymentDate: paymentDate || '',
                accountNumber: accountNumber || '',
                status: status || ''
            };
        });

        return summaryArray;
    };

    const findColumnValue = (row, headers, keywords) => {
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i].toLowerCase();
            if (keywords.some(keyword => header.includes(keyword.toLowerCase()))) {
                const value = row[headers[i]];
                if (value !== undefined && value !== null && value !== '') {
                    return String(value).trim();
                }
            }
        }
        return null;
    };

    const handleUpload = async () => {
        if (!file || previewData.length === 0) {
            toast.error("No data to upload");
            return;
        }

        setUploading(true);

        try {
            const uploadPayload = {
                summary: summary,
            };

            const response = await axios.post(
                'https://www.azzenterprises.com/api/reports/upload-reports',
                uploadPayload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true,
                    timeout: 30000
                }
            );

            if (response.data.success) {
                toast.success(`Successfully uploaded ${response.data.processed} records`);
                onUploadSuccess();
                handleClose();
            } else {
                toast.error(response.data.message || "Upload failed");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Upload failed";
            toast.error(errorMessage);
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setHeaders([]);
        setTotalRows(0);
        setSummary(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onHide();
    };

    const getFileIcon = () => {
        if (!file) return <FiFile size={16} />;
        if (file.name.match(/\.(xlsx|xls)$/)) {
            return <FiFile size={16} className="text-success" />;
        } else {
            return <FiFile size={16} className="text-primary" />;
        }
    };

    const truncateText = (text, maxLength = 50) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const getStatusVariant = (status) => {
        if (!status) return 'secondary';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('success') || statusLower.includes('completed') || statusLower.includes('paid')) {
            return 'success';
        } else if (statusLower.includes('fail') || statusLower.includes('error') || statusLower.includes('rejected')) {
            return 'danger';
        } else if (statusLower.includes('pending') || statusLower.includes('processing')) {
            return 'warning';
        }
        return 'secondary';
    };

    const shouldShowSummary = summary && totalRows > 0;

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton style={{ fontSize: "16px", padding: "1rem" }}>
                <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "18px" }}>
                    <FiUpload size={20} />
                    Upload Payment Reports
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ fontSize: "14px", padding: "1.5rem" }}>
                <div className="mb-4">
                    <Form.Group>
                        <Form.Label className="fw-semibold" style={{ fontSize: "14px" }}>Select File</Form.Label>
                        <Form.Control
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv,.txt"
                            onChange={handleFileChange}
                            disabled={uploading}
                            style={{ fontSize: "14px" }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                            Supported formats: Excel (.xlsx, .xls), CSV (.csv), Text (.txt)
                        </Form.Text>
                    </Form.Group>
                </div>

                {file && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            {getFileIcon()}
                            <span className="fw-semibold" style={{ fontSize: "14px" }}>{file.name}</span>
                            <Badge bg="secondary" style={{ fontSize: "12px" }}>
                                {(file.size / 1024).toFixed(2)} KB
                            </Badge>
                        </div>
                        {headers.length > 0 && (
                            <div style={{ fontSize: "12px" }} className="text-muted">
                                <strong>Detected:</strong> {headers.length} columns × {totalRows} rows
                            </div>
                        )}
                    </div>
                )}

                {shouldShowSummary && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <FiCheckCircle className="text-primary" size={16} />
                            <span className="fw-semibold" style={{ fontSize: "14px" }}>Upload Summary</span>
                            <Badge bg="primary" style={{ fontSize: "12px" }}>
                                {summary.length} records
                            </Badge>
                        </div>
                        <div style={{ fontSize: "12px" }} className="text-muted mb-2">
                            <strong>Format:</strong> Payee Name - Payment Date - Account Number - Status
                        </div>
                        <div style={{ maxHeight: '200px', overflow: 'auto' }} className="fw-bold">
                            {summary.map((item, index) => {
                                const statusColor = item.status?.toLowerCase() === 'approved' ? 'text-success' : 'text-danger';
                                return (
                                    <div key={index} className="border-bottom pb-1 mb-1" style={{ fontSize: "13px" }}>
                                        <span className="text-muted">{index + 1}. </span> {item.payeeName || 'No Name'} - {item.paymentDate || 'No Date'} - {item.accountNumber || 'No Account'} - <span className={statusColor}>{item.status || 'No Status'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {previewData.length > 0 && headers.length > 0 && (
                    <div className="mb-3">
                        <h6 className="fw-semibold mb-2" style={{ fontSize: "14px" }}>
                            Data Preview ({previewData.length} of {totalRows} rows)
                        </h6>
                        <div style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                            <Table striped bordered hover size="sm" responsive style={{ fontSize: "12px", margin: 0 }}>
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                    <tr>
                                        <th style={{ fontSize: "12px", width: '60px', backgroundColor: '#f8f9fa', fontWeight: '600' }}>
                                            #
                                        </th>
                                        {headers.map((header, index) => (
                                            <th key={index} className="text-nowrap" style={{ fontSize: "12px", backgroundColor: '#f8f9fa', fontWeight: '600', minWidth: '120px' }}>
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, index) => (
                                        <tr key={index}>
                                            <td className="fw-semibold text-center" style={{ fontSize: "12px", backgroundColor: '#f8f9fa' }}>
                                                {row._rowNumber}
                                            </td>
                                            {headers.map((header, colIndex) => (
                                                <td key={colIndex} style={{ fontSize: "12px", maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row[header] || ''}>
                                                    {truncateText(row[header])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {previewData.length < totalRows && (
                            <div className="text-muted text-center mt-2" style={{ fontSize: "12px" }}>
                                Showing first {previewData.length} rows of {totalRows} total rows
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer style={{ fontSize: "14px", padding: "1rem 1.5rem" }}>
                <Button variant="outline-secondary" onClick={handleClose} disabled={uploading} style={{ fontSize: "14px" }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleUpload} disabled={!file || uploading || previewData.length === 0} className="d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                    {uploading ? (
                        <>
                            <Spinner animation="border" size="sm" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <FiUpload size={16} />
                            Upload {totalRows} Records
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UploadModal;